<!-- src/views/OpeningHoursView.html -->
<div class="view-container">
  <div class="header-section">
    <div>
      <h1 class="view-title">Gestione Aperture</h1>
      <p class="view-desc">In questa sezione potrai definire le aperture settimanali e le chiusure straordinarie.</p>
    </div>
    <button v-if="activeTab === 'closures'" class="btn-primary rounded-soft" @click="openClosureCreate">
      <Plus :size="18" />
      Nuova Chiusura
    </button>
  </div>

  <div class="tabs-nav border-bottom">
    <button class="tab-btn" :class="{ 'active': activeTab === 'weekly' }" @click="activeTab = 'weekly'">
      <CalendarDays :size="18" />
      Orari Settimanali
    </button>
    <button class="tab-btn" :class="{ 'active': activeTab === 'closures' }" @click="activeTab = 'closures'">
      <CalendarX :size="18" />
      Chiusure Straordinarie
    </button>
  </div>

  <div v-if="activeTab === 'weekly'" class="tab-content">
    <div class="table-container rounded-soft shadow-soft">
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 20%">Giorno</th>
            <th style="width: 15%">Stato</th>
            <th style="width: 50%">Fasce Orarie</th>
            <th style="width: 15%; text-align: right;">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="weeklyDays.length === 0">
            <td colspan="4" class="empty-state">Nessun orario settimanale configurato.</td>
          </tr>
          <tr v-for="day in weeklyDays" :key="day.id">
            <td class="font-medium text-light">{{ day.name }}</td>
            <td>
              <span class="status-badge" :class="day.isOpen ? 'bg-success' : 'bg-muted'">
                {{ day.isOpen ? 'Aperto' : 'Chiuso' }}
              </span>
            </td>
            <td class="text-muted">{{ formatSlots(day.slots, day.isOpen) }}</td>
            <td class="actions-cell">
              <button class="btn-icon rounded-soft btn-edit" @click="openWeeklyDrawer(day)" title="Modifica">
                <Edit :size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div v-if="activeTab === 'closures'" class="tab-content">
    <div class="table-container rounded-soft shadow-soft">
      <table class="data-table">
        <thead>
          <tr>
            <th>Occasione</th>
            <th>Data</th>
            <th>Dettagli Orario</th>
            <th style="text-align: right;">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="closures.length === 0">
            <td colspan="4" class="empty-state">Nessuna chiusura straordinaria configurata.</td>
          </tr>
          <tr v-for="closure in closures" :key="closure.id">
            <td class="font-medium text-light">{{ closure.name || closure.occasione }}</td>
            <td class="text-muted">{{ formatDate(closure.date || closure.data) }}</td>
            <td class="text-muted">
              {{ (closure.all_day || closure.isFullDay) ? 'Tutto il giorno' : `${closure.start} - ${closure.end}` }}
            </td>
            <td class="actions-cell">
              <button class="btn-icon rounded-soft btn-edit" @click="openClosureEdit(closure)" title="Modifica">
                <Edit :size="16" />
              </button>
              <button class="btn-icon rounded-soft btn-delete" @click="deleteClosure(closure.id)" title="Cancella">
                <Trash2 :size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="drawer-overlay" :class="{ 'is-visible': isWeeklyDrawerOpen }" @click="closeWeeklyDrawer"></div>
  <div class="drawer" :class="{ 'is-open': isWeeklyDrawerOpen }">
    <div class="drawer-header border-bottom">
      <h2>Modifica {{ editDay?.name }}</h2>
      <button class="btn-close" @click="closeWeeklyDrawer">
        <X :size="20" />
      </button>
    </div>
    <div class="drawer-body" v-if="editDay">
      <form @submit.prevent="saveWeeklyDay" id="weekly-form">
        <div class="form-group toggle-group">
          <label class="toggle-label text-light">Stato di Apertura</label>
          <label class="switch">
            <input type="checkbox" v-model="editDay.isOpen">
            <span class="slider round"></span>
          </label>
          <span class="toggle-status" :class="editDay.isOpen ? 'text-success' : 'text-muted'">
            {{ editDay.isOpen ? 'Aperto' : 'Chiuso' }}
          </span>
        </div>

        <div v-if="editDay.isOpen" class="slots-container">
          <div class="slots-header border-bottom">
            <h3 class="text-light">Fasce Orarie</h3>
            <button type="button" class="btn-fast-action rounded-soft" @click="addSlot">
              <Plus :size="14" /> Aggiungi Fascia
            </button>
          </div>

          <div class="slots-list">
            <div v-if="editDay.slots.length === 0" class="empty-slots text-muted">
              Nessuna fascia oraria definita. Clicca su "Aggiungi Fascia" per iniziare.
            </div>
            <div v-for="(slot, idx) in editDay.slots" :key="idx" class="slot-row">
              <div class="form-group mb-0">
                <label :for="'start-'+idx">Inizio</label>
                <input type="time" :id="'start-'+idx" v-model="slot.start" required />
              </div>
              <div class="form-group mb-0">
                <label :for="'end-'+idx">Fine</label>
                <input type="time" :id="'end-'+idx" v-model="slot.end" required />
              </div>
              <button type="button" class="btn-icon rounded-soft btn-delete slot-delete" @click="removeSlot(idx)">
                <Trash2 :size="16" />
              </button>
            </div>
          </div>

          <div v-if="dayError" class="alert-error mt-3">
            <AlertCircle :size="16" />
            <span>{{ dayError }}</span>
          </div>
        </div>
      </form>
    </div>
    <div class="drawer-footer border-top">
      <button type="button" class="btn-secondary rounded-soft" @click="closeWeeklyDrawer">Annulla</button>
      <button type="submit" form="weekly-form" class="btn-primary rounded-soft">Salva</button>
    </div>
  </div>

  <div class="drawer-overlay" :class="{ 'is-visible': isClosureDrawerOpen }" @click="closeClosureDrawer"></div>
  <div class="drawer" :class="{ 'is-open': isClosureDrawerOpen }">
    <div class="drawer-header border-bottom">
      <h2>{{ isEditingClosure ? 'Modifica Chiusura' : 'Nuova Chiusura' }}</h2>
      <button class="btn-close" @click="closeClosureDrawer">
        <X :size="20" />
      </button>
    </div>
    <div class="drawer-body">
      <form @submit.prevent="saveClosure" id="closure-form">
        <div class="form-group">
          <label for="occasione">Occasione *</label>
          <input type="text" id="occasione" v-model="closureForm.occasione" required placeholder="Esempio: Festa del Patrono" />
        </div>

        <div class="form-group">
          <label for="dataChiusura">Data *</label>
          <input type="date" id="dataChiusura" v-model="closureForm.data" required />
        </div>

        <div class="form-group toggle-group mt-3">
          <label class="toggle-label text-light">Tutto il giorno</label>
          <label class="switch">
            <input type="checkbox" v-model="closureForm.isFullDay">
            <span class="slider round"></span>
          </label>
        </div>

        <div v-if="!closureForm.isFullDay" class="form-row mt-3 border-top pt-3">
          <div class="form-group half-width mb-0">
            <label for="c-start">Ora Inizio</label>
            <input type="time" id="c-start" v-model="closureForm.start" required />
          </div>
          <div class="form-group half-width mb-0">
            <label for="c-end">Ora Fine</label>
            <input type="time" id="c-end" v-model="closureForm.end" required />
          </div>
        </div>

        <div v-if="customClosureError" class="alert-error mt-3">
          <AlertCircle :size="16" />
          <span>{{ customClosureError }}</span>
        </div>
      </form>
    </div>
    <div class="drawer-footer border-top">
      <button type="button" class="btn-secondary rounded-soft" @click="closeClosureDrawer">Annulla</button>
      <button type="submit" form="closure-form" class="btn-primary rounded-soft">Salva</button>
    </div>
  </div>
</div>